// Unmeasured imagery, as a render rule fed by the coverage the bake wrote.
//
// An albedo tile's alpha and the orrery globe map's alpha are the same field:
// 1 where an instrument measured the texel, 0 where none did (the bake writes
// no alpha plane at all on a body whose mosaic covers the sphere, which reads
// back as 1). The RGB under a hole already carries the fill the bake derived
// from the ground the same product measured, so drawing the texel as it
// stands IS the honest answer - which makes this chunk the identity until
// something asks unmeasured ground to draw as some other colour. That is what
// `descent-dojo --inject nodata-black` and `--inject fill-row` ask for, and it
// is the only reason the coverage reading can fail: a mask that were
// all-measured would leave the injected colour with nothing to paint.
//
// The substitution REPLACES the texel instead of adding a difference against
// the colour the bake is believed to have used. An addition needs the runtime
// to know that colour, and above the streaming edge - where the globe map is
// what draws the body and no pyramid is open - it does not. Getting it wrong
// there was not a subtle error either: the sum went negative, ACES filmic
// tone mapping takes a negative radiance back up to white, and
// `--inject nodata-black` painted Pluto's unmeasured cap BRIGHTER than the
// ground beside it.

import * as THREE from 'three';

import { albedoByteTable, albedoFillTransferOf, type AlbedoTransfer } from './albedoTransfer.ts';

/** The two uniforms the chunk reads, owned by whoever drew the body. */
export interface CoverageFillRef {
  readonly unmeasured: { value: THREE.Color };
  /** 1 while an override is armed, 0 for the texel as the bake wrote it. */
  readonly on: { value: number };
}

export interface CoverageFillHandle {
  readonly ref: CoverageFillRef;
  /** The fill the bake actually wrote under this body's holes, for a reader
   *  that has to report what the frame draws there. */
  setBody(bakedFill: string, transfer?: AlbedoTransfer | null): void;
  /** What unmeasured imagery draws instead, or null for the texel itself. */
  setUnmeasured(cssColor: string | null): void;
  /** What it is drawing now, as `#rrggbb`, for a reader that has to report it. */
  unmeasuredHex(): string;
}

/**
 * A bake's fill hex as the colour the shader mixes in. On a linear-reflectance
 * body the hex is a DN, like the ground it was cut from, so it takes the same
 * decode the ground does and not the sRGB one `Color.set` would give it.
 */
function fillColor(target: THREE.Color, hex: string, transfer: AlbedoTransfer | null | undefined): void {
  target.set(hex);
  const live = albedoFillTransferOf(transfer ?? null);
  if (!live) return;
  const table = albedoByteTable(live);
  const dn = target.getHex(THREE.SRGBColorSpace);
  target.setRGB(table[(dn >> 16) & 255]!, table[(dn >> 8) & 255]!, table[dn & 255]!,
    THREE.LinearSRGBColorSpace);
}

export function createCoverageFill(bakedFill: string): CoverageFillHandle {
  const ref: CoverageFillRef = {
    unmeasured: { value: new THREE.Color(bakedFill) },
    on: { value: 0 },
  };
  let baked = bakedFill;
  let bakedTransfer: AlbedoTransfer | null = null;
  let override: string | null = null;
  return {
    ref,
    setBody(next: string, transfer: AlbedoTransfer | null = null): void {
      baked = next;
      bakedTransfer = transfer;
      if (override === null) fillColor(ref.unmeasured.value, next, transfer);
    },
    setUnmeasured(next: string | null): void {
      override = next;
      ref.on.value = next === null ? 0 : 1;
      if (next === null) fillColor(ref.unmeasured.value, baked, bakedTransfer);
      else ref.unmeasured.value.set(next);
    },
    unmeasuredHex(): string { return `#${ref.unmeasured.value.getHexString()}`; },
  };
}

/**
 * Inject the chunk into a `MeshStandardMaterial`'s compiled shader.
 *
 * Placed straight after `map_fragment`, which is where `diffuseColor.a` holds
 * the sampled texel's alpha times the material's opacity - the tile and globe
 * materials are both opaque, so it is the coverage itself. The alpha is put
 * back to 1 afterwards because a coverage number has no business reaching the
 * blend stage. Injected BEFORE any chunk that replaces `map_fragment` itself,
 * so the substitution stays on the sampled texel's own alpha.
 */
export function applyCoverageFill(shader: THREE.WebGLProgramParametersWithUniforms,
  ref: CoverageFillRef): void {
  shader.uniforms['fxUnmeasured'] = ref.unmeasured;
  shader.uniforms['fxUnmeasuredOn'] = ref.on;
  shader.fragmentShader = `uniform vec3 fxUnmeasured;\nuniform float fxUnmeasuredOn;\n${shader.fragmentShader}`
    .replace('#include <map_fragment>',
      '#include <map_fragment>\n'
      + '  diffuseColor.rgb = mix(diffuseColor.rgb, fxUnmeasured,\n'
      + '    fxUnmeasuredOn * (1.0 - diffuseColor.a));\n'
      + '  diffuseColor.a = 1.0;');
}
